import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import OkCancelModal from '@/shared/components/OkCancelModal';
import CurrencySymbol from '@/shared/components/CurrencySymbol';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { isValidNumber } from '@/shared/utils/validationHelpers';
import messages from './assignmentTemplateMessages';

class EditAssignmentTemplateInvoiceScheduleModal extends Component {
  constructor(props) {
    super(props);
    const { template } = props;
    this.state = {
      invoiceSchedules: [],
      invoiceScheduleId: template.invoiceScheduleId || null,
      amount: template.amount,
      ledger: template.ledger || '',
      costLedger: template.costLedger || '',
      saving: false,
      showValidation: false,
      amountBadInput: false,
    };
  }

  componentDidMount() {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.api}/api/invoiceschedule/get`).then(
      ({ data }) => {
        this.setState({
          invoiceSchedules: (data || [])
            .map(t => ({
              ...t,
              label: `${intl.formatMessage(
                listItemMsg('invoiceSchedule', t.id, t.name),
              )}          (${intl.formatMessage(listItemMsg('InvoicingType', t.invoicingType))})`,
            }))
            .sort(dynamicSort('label')),
        });
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  }

  invoiceScheduleChanged = e => {
    this.setState({ invoiceScheduleId: e.target.value || null });
  };

  isAmountValid = () => {
    const { invoiceSchedules, invoiceScheduleId, amount, amountBadInput } = this.state;
    const schedule = invoiceScheduleId
      ? invoiceSchedules.find(x => x.id === invoiceScheduleId)
      : null;
    const showAmount = !!schedule && schedule.invoicingType !== 0;
    if (!showAmount) return true; // amount not used
    if (amountBadInput) return false; // malformed text in the field
    if (amount === null || amount === undefined) return true; // optional
    return isValidNumber(amount) && amount > 0; // if filled, must be > 0
  };

  save = () => {
    const { onClose, intl, template } = this.props;
    const { saving, invoiceSchedules, invoiceScheduleId, amount, ledger, costLedger } = this.state;
    if (saving) return undefined;
    if (!this.isAmountValid()) {
      this.setState({ showValidation: true });
      return undefined;
    }

    const schedule = invoiceScheduleId
      ? invoiceSchedules.find(x => x.id === invoiceScheduleId)
      : null;
    const useAmount = !!schedule && schedule.invoicingType !== 0;

    const model = {
      id: template.id,
      invoiceScheduleId: invoiceScheduleId || null,
      amount: useAmount ? amount : null,
      ledger,
      costLedger,
    };

    this.setState({ saving: true });
    return api.base.put(`${config.appUrls.api}/api/AssignmentTemplate/InvoiceSchedule`, model).then(
      () => {
        onClose({
          invoiceScheduleId: model.invoiceScheduleId,
          invoiceScheduleName: schedule ? schedule.name : null,
          amount: model.amount,
          ledger: model.ledger,
          costLedger: model.costLedger,
        });
      },
      error => {
        handleHttpError(intl)(error);
        this.setState({ saving: false });
      },
    );
  };

  render() {
    const { intl, onClose } = this.props;
    const {
      invoiceSchedules,
      invoiceScheduleId,
      amount,
      ledger,
      costLedger,
      saving,
      showValidation,
    } = this.state;

    const schedule = invoiceScheduleId
      ? invoiceSchedules.find(x => x.id === invoiceScheduleId)
      : null;
    const showAmount = !!schedule && schedule.invoicingType !== 0;
    const amountInvalid = showValidation && !this.isAmountValid();

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.EditInvoiceSchedule)}
        show
        onClose={() => onClose(null)}
        onSave={this.save}
        disabled={saving || amountInvalid}
        onHoverSave={() => this.setState({ showValidation: true })}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.InvoiceSchedule} />
            </label>
            <div className="col-sm-5">
              <select
                onChange={this.invoiceScheduleChanged}
                className="form-control"
                value={invoiceScheduleId || ''}
              >
                <option value="">{intl.formatMessage(messages.NoInvoiceSchedule)}</option>
                {invoiceSchedules.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showAmount && (
            <div className={classNames('form-group', { 'has-error': amountInvalid })}>
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.Amount} />
              </label>
              <div className="col-sm-5">
                <div className="input-group">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={amount ?? ''}
                    onChange={e =>
                      this.setState({
                        amount: e.target.value === '' ? null : parseFloat(e.target.value),
                        amountBadInput: e.target.validity.badInput,
                      })
                    }
                    className="form-control"
                  />
                  <span className="input-group-addon">
                    <CurrencySymbol />
                  </span>
                </div>
              </div>
              {amountInvalid && (
                <span className="col-sm-offset-4 col-sm-8 help-block">
                  <FormattedMessage {...messages.AmountMustBeGreaterThanZero} />
                </span>
              )}
            </div>
          )}
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.LedgerAccount} />
            </label>
            <div className="col-sm-5">
              <input
                type="text"
                className="form-control"
                name="ledgeraccount"
                maxLength="10"
                value={ledger}
                onChange={e => this.setState({ ledger: e.target.value })}
                id="ledgeraccount"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.CostLedgerAccount} />
            </label>
            <div className="col-sm-5">
              <input
                type="text"
                className="form-control"
                name="costLedgerAccount"
                maxLength="10"
                value={costLedger}
                onChange={e => this.setState({ costLedger: e.target.value })}
                id="costLedgerAccount"
              />
            </div>
          </div>
        </div>
      </OkCancelModal>
    );
  }
}

EditAssignmentTemplateInvoiceScheduleModal.propTypes = {
  intl: intlShape.isRequired,
  template: PropTypes.shape({
    id: PropTypes.string,
    invoiceScheduleId: PropTypes.string,
    invoiceScheduleName: PropTypes.string,
    amount: PropTypes.number,
    ledger: PropTypes.string,
    costLedger: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default injectIntl(EditAssignmentTemplateInvoiceScheduleModal);
